import { Injectable } from '@angular/core';
import jspdf from 'jspdf';
import { IUserPreferencesResponse } from 'src/app/shared/models/IUserDetails';
import { LoaderService, RequestService } from 'src/app/shared/services';
import { Constants } from '../../../../shared/constant/constants';
import { SessionManagerService } from '../../../../shared/services/session/session-manager.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MoveFolderComponent } from '../toolbar-header/move-folder/move-folder.component';
import { db, cvUserPrefs, hidePageBreaks, resetScale, showPageBreaks, stage } from 'src/assets/js/draw';
import { FirebaseAuthService } from 'src/app/features/user/auth/services/firebase-auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FileManagerService } from '../../services/file-manager.service';
import { PRINT_FILE, USER_HOME } from '../../../../shared/constant/routes';
import { ONE_PAGE_LENGTH, ONE_PAGE_WIDTH } from 'src/app/shared/constant/drawConstants';
import { CV_CNTL_AA } from 'src/app/shared/constant/canvasSymTypes';
import { AzureAuthService } from 'src/app/features/user/auth/services/azure-auth.service';
import { EmailPdfPopupComponent } from '../toolbar-header/email-pdf-popup/email-pdf-popup.component';
import { AssignmentService } from '../../services/assignment.service';
import { CommonService } from '../../../../shared/services/session/common.service';

@Injectable()
export class ToolbarService {
  fileName!: string;
  constructor(
    private readonly router: Router,
    private readonly requestService: RequestService,
    private readonly sessionManagerService: SessionManagerService,
    private readonly modalService: BsModalService,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly azureAuth: AzureAuthService,
    private readonly fileManagerService: FileManagerService,
    private loader: LoaderService,
    private activatedRoute: ActivatedRoute,
    private assignmentService: AssignmentService,
    private readonly commonService: CommonService,
  ) {
    this.fileManagerService.currentFileName$.subscribe(res => {
      this.fileName = res;
    });
  }

  /**
   * @description Function to generate dataurl from canvas
   */
  generateDataUrlFromCanvas(): { image: string; canvasHeight: number; pageCount: number } {
    const oldScaleX = stage.scaleX;
    const oldScaleY = stage.scaleY;
    const oldWidth = stage.canvas.width;
    const oldHeight = stage.canvas.height;
    const pages = hidePageBreaks();
    const canvasHeight = ONE_PAGE_LENGTH * pages;
    resetScale(1, 1, ONE_PAGE_WIDTH, canvasHeight);
    const canvas = document.getElementById('drawingboard') as HTMLCanvasElement;
    const image = canvas.toDataURL('image/png');
    resetScale(oldScaleX, oldScaleY, oldWidth, oldHeight);
    showPageBreaks();
    return { image, canvasHeight, pageCount: db.pageCount };
  }
  async generateBlob(): Promise<Blob | null> {
    return new Promise(resolve => {
      const canvas = document.getElementById('drawingboard') as HTMLCanvasElement;
      canvas.toBlob(res => {
        resolve(res);
      }, 'canavas-image');
    });
  }

  /**
   * @description Function to generate pdf from canvas
   */
  async generatePdfFromCanvas(isEmail = false) {
    this.loader.start();

    // Wait for canvas to render
    await new Promise(resolve => setTimeout(resolve, 150));

    const data = this.generateDataUrlFromCanvas();
    const image = data.image;
    const imgWidth = 210; // image width in mm
    const pageHeight = 295; // page height in mm
    const imgHeight = (data.canvasHeight * imgWidth) / ONE_PAGE_WIDTH;
    let pages = 2;
    let heightLeft = imgHeight;

    let position = 0;
    const pdf = new jspdf('p', 'mm'); // Generates PDF in potrait mode
    pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'SLOW');
    heightLeft -= pageHeight;
    // change to use db value for pagecount since only canvas knows of pages added by user
    while (pages <= db.pageCount) {
      //this.fileManagerService.totalPageInPdf) {
      position = heightLeft - imgHeight;
      pdf.addPage('a4', 'p');
      pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'SLOW');
      heightLeft -= pageHeight;
      pages++;
    }

    if (isEmail) {
      // Create Blob from PDF data
      const pdfBlob = pdf.output('blob');
      // Store Blob in localStorage as base64 string (optional, for email attachment)
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        localStorage.setItem('emailAttachment', JSON.stringify(result));
      };
      reader.readAsDataURL(pdfBlob);
      this.loader.stop();
      return this.nameForDownload();
    }

    await pdf.save(`${this.nameForDownload()}.pdf`, { returnPromise: true });

    this.loader.stop();
    this.openGoodJobPopUp();
    return;
  }

  async getCurrentFileDataAsBlob() {
    // Wait for the canvas to render
    await new Promise(resolve => setTimeout(resolve, 150));

    const data = this.generateDataUrlFromCanvas();
    const image = data.image;
    const imgWidth = 210; // Image width in mm
    const pageHeight = 295; // Page height in mm
    const imgHeight = (data.canvasHeight * imgWidth) / ONE_PAGE_WIDTH;
    let pages = 2;
    let heightLeft = imgHeight;

    let position = 0;
    const pdf = new jspdf('p', 'mm'); // Generate PDF in portrait mode
    pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'SLOW');
    heightLeft -= pageHeight;

    // Add pages dynamically based on `db.pageCount`
    while (pages <= db.pageCount) {
      position = heightLeft - imgHeight;
      pdf.addPage('a4', 'p');
      pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'SLOW');
      heightLeft -= pageHeight;
      pages++;
    }

    // Generate a Blob from the PDF data
    const pdfBlob = pdf.output('blob');

    // Return the Blob to upload to AWS
    return pdfBlob;
  }

  generateImageFromCanvas(googleDriveFileContentType: any) {
    const oldScaleX = stage.scaleX;
    const oldScaleY = stage.scaleY;
    const oldWidth = stage.canvas.width;
    const oldHeight = stage.canvas.height;
    const pages = hidePageBreaks();
    const canvasHeight = ONE_PAGE_LENGTH * pages;
    resetScale(1, 1, ONE_PAGE_WIDTH, canvasHeight);
    const canvas = document.getElementById('drawingboard') as HTMLCanvasElement;
    const image1 = canvas.toDataURL(googleDriveFileContentType);
    resetScale(oldScaleX, oldScaleY, oldWidth, oldHeight);
    showPageBreaks();
    return image1;
  }

  generatePdfFromCanvasForGoogleDrive() {
    const data = this.generateDataUrlFromCanvas();
    const image = data.image;
    const imgWidth = 210; // image width in mm
    const pageHeight = 295; // page height in mm
    const imgHeight = (data.canvasHeight * imgWidth) / ONE_PAGE_WIDTH;
    let pages = 2;
    let heightLeft = imgHeight;

    let position = 0;
    const pdf = new jspdf('p', 'mm'); // Generates PDF in potrait mode
    pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    // change to use db value for pagecount since only canvas knows of pages added by user
    while (pages <= db.pageCount) {
      //this.fileManagerService.totalPageInPdf) {
      position = heightLeft - imgHeight;
      pdf.addPage('a4', 'p');
      pdf.addImage(image, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      pages++;
    }
    //Convert PDF Data to DataURISTRING
    const pdfData = pdf.output('datauristring');
    return pdfData;
  }

  /**
   * @description Function to generate png from canvas
   */
  generatePngFromCanvas() {
    this.loader.start();
    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('download', `${this.nameForDownload()}.png`);
    const url = this.generateDataUrlFromCanvas().image.replace(/^data:image\/png/, 'data:application/octet-stream');
    downloadLink.setAttribute('href', url);
    //console.log('url');
    downloadLink.click();
    this.loader.stop();
    this.openGoodJobPopUp();
  }

  nameForDownload() {
    let baseName = this.fileName;
    if (this.fileName.includes('Math File')) {
      baseName = 'Math File';
    }
    return this.stripExtension(baseName);
  }
  // to remove the extension of file
  stripExtension(fileName: any) {
    return fileName.replace(/\.[^/.]+$/, '');
  }

  /**
   * @description Function to print pdf from canvas
   * When offline, opens a new blank tab and writes the canvas image directly
   * into it (avoids loading the full Angular app which requires network).
   * When online, preserves existing window.open behavior with the print-canvas route.
   */
  async printPdf() {
    console.log('navigator.onLine:', navigator.onLine);
    if (!navigator.onLine) {
      console.log('Offline detected using printOfflineInNewTab()');
      this.printOfflineInNewTab();
      return;
    }

    console.log('Online detected using existing window.open flow');
    const { image } = this.generateDataUrlFromCanvas();
    console.log('Image data URL generated, length:', image.length);
    // localStorage.setItem('image', this.generateDataUrlFromCanvas().image);
    await this.saveImageToIndexedDB(image);
    console.log('Image saved to IndexedDB');
    const win1 = window.open(window.location.origin + PRINT_FILE.URL) as Window;
    console.log('window.open result:', win1 ? 'success' : 'blocked/null');
    if (win1) {
      win1.onafterprint = () => {
        console.log('onafterprint cleaning up');
        // localStorage.removeItem('image');
        this.removeImageFromIndexedDB();

        win1.close();
        this.openMoveTOFolderPOpup(false);
      };
      setTimeout(() => {
        console.log('Executing win1.print() after 2s delay');
        win1.print();
      }, 2000);
    }
  }

  /**
   * @description Offline print: opens a new browser tab and writes the canvas
   * image + print CSS directly into it. No route navigation or network needed.
   * Replicates the same print CSS from print-file.component.scss.
   */
  private printOfflineInNewTab(): void {
    console.log('Starting offline print flow');
    const { image } = this.generateDataUrlFromCanvas();
    console.log('Image data URL generated, length:', image.length);

    const printWindow = window.open('about:blank', '_blank') as Window;
    if (!printWindow) {
      console.error('window.open returned null — popup may be blocked');
      return;
    }
    console.log('New tab opened successfully!!');

    try {
      printWindow.history.replaceState(null, '', PRINT_FILE.URL);
      console.log('URL bar set to', PRINT_FILE.URL);
    } catch (e) {
      console.warn('Could not set URL bar:', e);
    }

    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Print - KiwiWrite</title>
  <style>
    * { margin: 0; padding: 0; }
    body { background: #fff; }
    img { width: 100%; margin: 0; display: block; }
    @page { size: A4 portrait; margin: 0mm 0mm 1mm 0mm; padding: 0mm 0mm 1mm 0mm; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <img src="${image}" alt="Math file preview" />
</body>
</html>`);
    printWindow.document.close();
    console.log('HTML written to new tab');

    const triggerPrint = () => {
      console.log('Working print dialog');
      printWindow.focus();
      printWindow.print();

      printWindow.onafterprint = () => {
        console.log('onafterprint closing print tab');
        printWindow.close();
        if (navigator.onLine) {
          this.openMoveTOFolderPOpup(false);
        }
      };
    };

    const img = printWindow.document.querySelector('img');
    console.log('Image element found:', !!img, '| img.complete:', img?.complete);
    if (img) {
      if (img.complete) {
        console.log('Image already loaded → Working print immediately');
        triggerPrint();
      } else {
        console.log('Waiting for image onload');
        img.onload = () => {
          console.log('Image onload fired');
          triggerPrint();
        };
        img.onerror = err => {
          console.error('Image failed to load in new tab:', err);
          printWindow.close();
        };
      }
    } else {
      console.error('No img element found in new tab document');
    }
  }
  getImageFromIndexedDB(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open('imagesDatabase', 1);

      request.onsuccess = event => {
        const db = (event.target as IDBRequest).result;
        const transaction = db.transaction('images', 'readonly');
        const store = transaction.objectStore('images');

        const getRequest = store.get('image');

        getRequest.onsuccess = () => {
          resolve(getRequest.result ? getRequest.result.image : null);
        };

        getRequest.onerror = () => {
          reject('Error retrieving image from IndexedDB');
        };
      };

      request.onerror = () => {
        reject('Error opening IndexedDB');
      };
    });
  }
  async saveImageToIndexedDB(image: string) {
    const request = window.indexedDB.open('imagesDatabase', 1);

    request.onupgradeneeded = event => {
      const db = (event.target as IDBRequest).result;
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images', { keyPath: 'id' });
      }
    };

    request.onsuccess = event => {
      const db = (event.target as IDBRequest).result;
      const transaction = db.transaction('images', 'readwrite');
      const store = transaction.objectStore('images');

      const imageObject = { id: 'image', image };
      store.put(imageObject); // Store the image

      transaction.oncomplete = () => {
        console.log('Image saved to IndexedDB');
      };
    };

    request.onerror = event => {
      console.error('Error opening IndexedDB:', event);
    };
  }
  removeImageFromIndexedDB() {
    const request = window.indexedDB.open('imagesDatabase', 1);

    request.onsuccess = event => {
      const db = (event.target as IDBRequest).result;
      const transaction = db.transaction('images', 'readwrite');
      const store = transaction.objectStore('images');

      const deleteRequest = store.delete('image'); // Assuming you want to delete the image with key "image1"

      deleteRequest.onsuccess = () => {
        console.log('Image removed from IndexedDB');
      };

      deleteRequest.onerror = () => {
        console.log('Error removing image from IndexedDB');
      };
    };

    request.onerror = event => {
      console.error('Error opening IndexedDB:', event);
    };
  }

  getUserPreferences() {
    if (!this.sessionManagerService.getUserPreferences()) {
      this.requestService.get<IUserPreferencesResponse>(Constants.ENDPOINTS.userPreferencesGet).subscribe(res => {
        let A_AA_status = res.data?.A_AA_status;
        if (!A_AA_status) {
          A_AA_status = CV_CNTL_AA;
        }

        res.data.A_AA_status = A_AA_status;
        this.sessionManagerService.updateAPreferences$.next(res.data.A_AA_status);

        const preferences = res.data
          ? res.data
          : {
              isDyslexic: false,
              hideDisplayGrid: false,
              fileReminders: false,
              fontSize: 3,
              fontSpacing: 1,
              cursorColorIndex: 1,
              isDoubleClick: false,
              isUseContrastColors: false,
              A_AA_status: A_AA_status,
              backgroundColor: '#FFFFFF',
              displayEqualInColor: false,
              language: 'en',
              isTabbingOn: false,
              cvMainColor: '#000000',
              cvOperatorsColor: '#000000',
              cvEqualsColor: '#000000',
              cvTextColor: '#000000',
              gridLineIndex: 1,
              useBoldMathDisplay: false,
              showMathNumberAndOperatorKeysInBoldStyle: false,
            };

        this.sessionManagerService.setUserPreferences(preferences);
        cvUserPrefs({ ...res.data });
        this.sessionManagerService.updateToolTipPreferences$.next(res.data?.showExtraToolTip || true);
        // Notify CommonService about tabbing preference to sync with TabbingService
        this.commonService.setIsTabbingOn(!!(preferences as any).isTabbingOn);
      });
    }
  }
  openMoveTOFolderPOpup(isExport = true) {
    if (!this.sessionManagerService.getUserPreferences()?.fileReminders) {
      const modalRef: BsModalRef = this.modalService.show(MoveFolderComponent);
      modalRef.content.isExport = isExport;
    }
  }

  closeOpenPopUp() {
    this.modalService.hide();
  }

  openGoodJobPopUp() {
    this.closeOpenPopUp();
    setTimeout(() => {
      this.openMoveTOFolderPOpup();
    }, 1000);
  }
  async logout() {
    const studentId =
      this.activatedRoute.snapshot.queryParams['studentFirebaseId'] || this.router.parseUrl(this.router.url).queryParams?.studentFirebaseId || null;

    if (!studentId) {
      this.fileManagerService.saveLastFileDetails().subscribe();
    }
    await this.fileManagerService.saveFileData();
    this.assignmentService.clearAssignmentFileData();

    this.requestService.patch(Constants.ENDPOINTS.logout).subscribe(() => {
      localStorage.removeItem('login');
      localStorage.removeItem('preferences');
      localStorage.removeItem('redirectionURL');
      this.sessionManagerService.logout();
      this.firebaseAuth.logOutfromFirebase();
      this.azureAuth.logOutfromAzure();
      this.sessionManagerService.setPriceId('free_trial' as string);

      this.router.navigate([USER_HOME.URL]).then(() => {
        this.fileManagerService.resetCurrentData();
      });
    });
  }

  /**
   * Shared session teardown: clears auth state, calls logout PATCH, redirects home.
   */
  private cleanUpSessionAndRedirect(): Promise<void> {
    return new Promise<void>(resolve => {
      let settled = false;
      const doCleanup = () => {
        if (settled) return;
        settled = true;

        localStorage.removeItem('login');
        localStorage.removeItem('preferences');
        localStorage.removeItem('redirectionURL');
        this.sessionManagerService.logout();
        this.firebaseAuth.logOutfromFirebase();
        this.azureAuth.logOutfromAzure();
        this.sessionManagerService.setPriceId('free_trial' as string);

        this.router.navigate([USER_HOME.URL]).then(() => {
          this.fileManagerService.resetCurrentData();
          resolve();
        });
      };

      // Guard: timeout if backend logout PATCH hangs or stutters
      const timer = setTimeout(() => doCleanup(), 3000);

      this.requestService.patch(Constants.ENDPOINTS.logout).subscribe(
        () => {
          clearTimeout(timer);
          doCleanup();
        },
        () => {
          clearTimeout(timer);
          doCleanup();
        },
      );
    });
  }

  async logoutWithOutSave() {
    await this.cleanUpSessionAndRedirect();
  }

  async getUserPreferencesData() {
    if (!this.sessionManagerService.getUserPreferences()) {
      const res = await this.requestService.get<IUserPreferencesResponse>(Constants.ENDPOINTS.userPreferencesGet).toPromise();

      let A_AA_status = res?.data?.A_AA_status;
      if (!A_AA_status) {
        A_AA_status = CV_CNTL_AA;
      }

      if (res?.data) {
        res.data.A_AA_status = A_AA_status;
        this.sessionManagerService.updateAPreferences$.next(res.data.A_AA_status);
      }

      const initPreference = {
        isDyslexic: false,
        hideDisplayGrid: false,
        fileReminders: false,
        fontSize: 3,
        fontSpacing: 1,
        isDoubleClick: false,
        isUseContrastColors: false,
        A_AA_status: A_AA_status,
        backgroundColor: '#FFFFFF',
        displayEqualInColor: false,
        language: 'en',
        cvMainColor: '#000000',
        cvOperatorsColor: '#000000',
        cvEqualsColor: '#000000',
        cvTextColor: '#000000',
        gridLineIndex: 1,
        useBoldMathDisplay: false,
        showMathNumberAndOperatorKeysInBoldStyle: false,
      };

      return res?.data || initPreference;
    }

    return this.sessionManagerService.getUserPreferences();
  }

  openEmailPdfPopup(fileName: string | undefined) {
    this.closeOpenPopUp();
    const initialState = { fileName: fileName };
    setTimeout(() => {
      this.modalService.show(EmailPdfPopupComponent, { initialState });
    }, 150);
  }
}
